/**
 * fastfive 시나리오 fixture
 *
 * 백엔드 metadata API(`useChatbot`)가 `scenarios` 필드를 내려주기 전까지
 * 임시로 사용하는 fixture. BE 응답이 시작되면 이 파일과 useChatbot의
 * fixture 주입 가드를 함께 제거하면 된다.
 *
 * 질문 스키마 (PreQuestionRefactored + InputFactory 호환):
 *   - type:     'single' | 'group'                       // 라우팅 키 (validator/factory)
 *   - question: '...'                                    // 질문 텍스트 (ChatBubble로 표시)
 *   - fields:   [{ inputType, key, label, placeholder, validation, warning, options }]
 *       · inputType: 'Text' | 'MultiSelect' | 'DoubleText'
 *       · Text 의 validation: { min, max }
 *       · MultiSelect 는 fields[0].options 사용
 *   - requestMessage: 마지막 질문에 위치 (normalizer 가 questions[last].requestMessage 를 읽음)
 *
 * 시나리오 스키마:
 *   - key: 프론트 분기 식별자 (불변)
 *   - label: 버튼 표시 텍스트
 *   - flow: 'product' | 'formOnly' | 'redirect'
 */

const officeRegionOptions = [
    '강남구',
    '서초구',
    '구로구',
    '종로구',
    '강서구',
    '관악구',
    '성동구',
    '용산구',
    '중구',
    '영등포구',
    '마포구',
    '경기 성남 분당구',
];

// ── 공통 질문 빌더 ──
const officeHeadcountQuestion = {
    type: 'single',
    question: '몇 명이 사용하실 사무실을 찾고 계신가요?',
    fields: [
        {
            inputType: 'Text',
            key: 'headcount',
            label: '인원 수 입력',
            placeholder: '인원 수를 입력해 주세요',
            validation: { min: 1, max: 999 },
            warning: '1~999명까지 입력 가능합니다.',
            unit: '명',
        },
    ],
};

const buildingHeadcountQuestion = {
    type: 'single',
    question: '사무실을 사용할 인원이 몇 명인가요?',
    fields: [
        {
            inputType: 'Text',
            key: 'headcount',
            label: '인원 수',
            placeholder: '예) 20',
            validation: { min: 1, max: 999 },
            warning: '1~999명까지 입력 가능합니다.',
            unit: '명',
        },
    ],
};

const regionQuestion = (requestMessage) => ({
    type: 'single',
    question: '사무실을 찾고 있는 지역을 모두 선택해 주세요.',
    fields: [
        {
            inputType: 'MultiSelect',
            key: 'region',
            options: officeRegionOptions,
        },
    ],
    requestMessage,
});

// 평수 질문은 마지막 단계라 requestMessage 자리지만 formOnly 시나리오에서는 사용되지 않음.
const createAreaQuestion = (question) => ({
    type: 'single',
    question,
    fields: [
        {
            inputType: 'Text',
            key: 'area',
            label: '평수',
            placeholder: '예) 30',
            validation: { min: 1, max: 99999 },
            warning: '1~99999평까지 입력 가능합니다.',
            unit: '평',
        },
    ],
});

export const fastfiveScenariosFixture = [
    {
        key: 'office',
        label: '공유오피스',
        flow: 'product',
        preQuestion: {
            questions: [
                { ...officeHeadcountQuestion, question: '사무실을 사용할 인원이 몇 명인가요?' },
                regionQuestion('조건에 맞는 사무실을 추천해주세요'),
            ],
        },
    },
    {
        key: 'building',
        label: '사옥 구축·임대오피스',
        flow: 'formOnly',
        formIntroMessage: '최저가 맞춤 견적을 준비해 곧 보내 드릴게요!',
        preQuestion: {
            questions: [
                buildingHeadcountQuestion,
                createAreaQuestion('사용할 공간의 면적을 입력해 주세요.'),
            ],
        },
    },
    {
        key: 'interior',
        label: '사무실 인테리어',
        flow: 'formOnly',
        formIntroMessage: '최저가 맞춤 견적을 준비해 곧 보내 드릴게요!',
        preQuestion: {
            questions: [createAreaQuestion('인테리어할 사무실 면적을 입력해 주세요.')],
        },
    },
    {
        key: 'lounge',
        label: '라운지 멤버십',
        flow: 'redirect',
        greetingMessage: '패스트파이브가 만든 1인 전용 라운지 멤버십 파이브스팟!\n아래 링크를 클릭해 더 자세히 확인해 보세요.',
        redirectUrl: '/lounge-membership',
        redirectButtonText: '라운지 멤버십 보러가기',
    },
    {
        key: 'private',
        label: '1인 오피스',
        flow: 'product',
        preQuestion: {
            questions: [
                officeHeadcountQuestion,
                regionQuestion('1인실 사무실을 추천해주세요'),
            ],
        },
    },
];

// fastfive 파트너 ID (Transaction.jsx의 isFF 판정과 동일)
const FASTFIVE_PARTNER_IDS = new Set([
    '6756ad3d3aaf5b928eec329b',
    '67615284c5ff44110dbc6613',
    '67516ecf103e7e29c75a621e',
]);

/**
 * useChatbot 응답에 fixture를 주입하는 헬퍼.
 * - fastfive 파트너이고
 * - 응답에 아직 scenarios 가 없으면
 * fixture를 끼워 넣어 반환한다. BE가 scenarios를 내려주기 시작하면
 * 두 번째 조건이 false가 되어 fixture는 자동으로 무시된다.
 */
export const withFastfiveScenariosFixture = (chatbotData, partnerId) => {
    if (!chatbotData) return chatbotData;
    if (!FASTFIVE_PARTNER_IDS.has(partnerId)) return chatbotData;
    if (Array.isArray(chatbotData.scenarios) && chatbotData.scenarios.length > 0) {
        return chatbotData;
    }
    return { ...chatbotData, scenarios: fastfiveScenariosFixture };
};

/**
 * 복원된 대화에서 시나리오 key 를 되찾는다.
 *
 * 대화는 서버에 남지만 scenarioKey 는 sessionStorage 에만 있다. 재방문(새 브라우저
 * 세션)이면 히스토리만 복원되고 scenarioKey 는 빈 채로 리드폼까지 가서
 * productType 이 빈 리드가 나간다.
 *
 * 사전질문을 마치면 시나리오의 requestMessage 가 사용자 메시지 끝에 붙어 전송되므로
 * (앞의 "사용 인원 N명, 선호 지역 X" 는 채팅 엔진이 붙인다) 접미사로 역산할 수 있다.
 * 시나리오를 바꿔 탄 경우를 위해 뒤에서부터 찾는다.
 * requestMessage 가 없는 formOnly 시나리오는 애초에 메시지를 남기지 않아 후보에서 빠진다.
 */
export const findFastfiveScenarioKeyFromHistory = (messages, scenarios) => {
    if (!Array.isArray(messages) || !Array.isArray(scenarios)) return null;

    const candidates = scenarios
        .map((scenario) => {
            const questions = scenario?.preQuestion?.questions;
            const last = Array.isArray(questions) ? questions[questions.length - 1] : null;
            return { key: scenario?.key, requestMessage: last?.requestMessage };
        })
        .filter(({ key, requestMessage }) => key && requestMessage);
    if (candidates.length === 0) return null;

    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const { message, isChatbot } = messages[i] || {};
        if (isChatbot || typeof message !== 'string') continue;
        const hit = candidates.find(({ requestMessage }) => message.endsWith(requestMessage));
        if (hit) return hit.key;
    }
    return null;
};
